import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  TextField,
  TextFieldRef,
  TextFieldSpecial,
} from 'components/Field/TextField';
import { TimescaleField } from 'components/Field/TimescaleField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { animateNext } from 'lib/animateNext';
import { theme } from 'lib/theme';
import { capitalize, sortBy } from 'lodash';
import React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import { doAseMakeSuggestion } from 'store/assessments/actions';
import {
  selectAseTplAnswersForQuestion,
  selectAseCategories,
  selectAseTplChecklistItemsForQuestion,
  selectAseDueTypes,
  selectAseSeverities,
  selectAseTplQuestion,
  selectAseTimescales,
} from 'store/assessments/selectors';
import { Action, FindingSuggestion } from 'store/assessments/types';
import {
  AssessmentQuestionType,
  DueDeterminedBy,
  TimescaleArray,
  TimescalePeriod,
} from 'types/types';

type EditableFinding = Pick<
  FindingSuggestion,
  | 'description'
  | 'why'
  | 'categoryId'
  | 'timescaleId'
  | 'severityId'
  | 'dueDeterminedBy'
  | 'dueTimescale'
  | 'dueTypeId'
  | 'cost'
>;

export const MakeSuggestionModal: React.FC<
  ModalScreenProps<ModalScreens.AseMakeSuggestion>
> = ({ route }) => {
  const dispatch = useDispatch();

  const {
    type,
    questionId,
    description,
    why,
    categoryId,
    timescaleId,
    severityId,
    dueDeterminedBy: incomingDueDeterminedBy,
    dueTimescale: incomingDueTimescale,
    dueTypeId,
    answerId,
    checklistItemId,
    cost,
  } = route.params;

  const costFieldRef = React.useRef<TextFieldRef>(null);
  const typeCapitalised = capitalize(type);

  const question = useSelector((state) =>
    selectAseTplQuestion(state, questionId),
  );
  const answers = useSelector((state) =>
    selectAseTplAnswersForQuestion(state, questionId),
  );
  const checklistItems = useSelector((state) =>
    selectAseTplChecklistItemsForQuestion(state, questionId),
  );

  const [currentlyEditing, setCurrentlyEditing] = React.useState<
    undefined | 'severity' | 'timescale' | 'due'
  >();

  const incomingValueKey = `assessment-make-finding-${type}`;
  const incomingValue = useSelector((state) =>
    selectIncomingValue(state, incomingValueKey),
  ) as Partial<EditableFinding> | undefined;

  const categoryKey: keyof Action = 'categoryId';

  const categories = useSelector(selectAseCategories);
  const severities = useSelector(selectAseSeverities);
  const timescales = useSelector(selectAseTimescales);
  const dueTypes = useSelector(selectAseDueTypes);

  const [finding, setFinding] = React.useState<EditableFinding>({
    description,
    why,
    categoryId: (categories[categoryId] || categories['0']!).id,
    timescaleId: (timescales[timescaleId] || timescales['0']!).id,
    severityId: (severities[severityId] || severities['0']!).id,
    dueDeterminedBy:
      incomingDueDeterminedBy !== DueDeterminedBy.type ||
      (dueTypeId && dueTypeId !== '1' && dueTypes[dueTypeId])
        ? incomingDueDeterminedBy
        : DueDeterminedBy.timescaleBased,
    dueTimescale:
      incomingDueDeterminedBy === DueDeterminedBy.timescale
        ? incomingDueTimescale || [1, TimescalePeriod.month]
        : null,
    dueTypeId:
      dueTypeId && dueTypeId !== '1' ? dueTypes[dueTypeId]?.id || null : null,
    cost,
  });

  const category = categories[finding.categoryId]!;
  const severity = severities[finding.severityId]!;
  const timescale = timescales[finding.timescaleId]!;
  const dueType = finding.dueTypeId ? dueTypes[finding.dueTypeId]! : null;

  const handleOnChangeWithName = React.useCallback(
    <T extends keyof EditableFinding>(name: T, value: EditableFinding[T]) =>
      setFinding((v: EditableFinding) => ({ ...v, [name]: value })),
    [],
  );

  const [selectedChecklistItemId, setSelectedChecklistItemId] =
    React.useState<number>(checklistItemId || 0);

  const [selectedAnswerId, setSelectedAnswerId] = React.useState<string>(
    answerId || '',
  );

  const handleSave = () => {
    if (question.type === AssessmentQuestionType.checklist) {
      if (!selectedChecklistItemId) {
        dispatch(
          ALERT({
            text: `Choose the checklist item the ${type} should be suggested for`,
          }),
        );
      }
    } else if (type === 'action' && !selectedAnswerId) {
      dispatch(
        ALERT({
          text: `Choose the answer the ${type} should be suggested for`,
        }),
      );
    }
    dispatch(
      doAseMakeSuggestion({
        ...finding,
        type,
        questionId,
        answerId: selectedAnswerId || '',
        checklistItemId: selectedChecklistItemId || 0,
      }),
    );
  };

  const openCategorySelect = React.useCallback(
    () =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            title: 'Category',
            fieldName: categoryKey,
            incomingValueKey,
            returnType: 'string',
            value: finding.categoryId,
            options: sortBy(
              Object.values(categories).map((cat) => ({
                name: cat.name,
                groupName: cat.group_name,
                internalValue: cat.id,
                ordering:
                  (cat.group_name === 'Uncategorised' ? 0 : cat.group_name) +
                  cat.name,
              })),
              ['ordering'],
            ),
          },
        }),
      ),
    [categories, dispatch, finding.categoryId, incomingValueKey],
  );

  const handleSetDue = (newCat: DueDeterminedBy, newValue: string) => {
    if (newCat === DueDeterminedBy.timescaleBased) {
      setFinding((v) => ({
        ...v,
        dueDeterminedBy: DueDeterminedBy.timescaleBased,
        dueTimescale: null,
        dueTypeId: null,
      }));
    } else if (newCat === DueDeterminedBy.timescale) {
      setFinding((v) => ({
        ...v,
        dueDeterminedBy: DueDeterminedBy.timescale,
        dueTimescale: finding.dueTimescale || [1, TimescalePeriod.month],
        dueTypeId: null,
      }));
    } else {
      setFinding((v) => ({
        ...v,
        dueDeterminedBy: DueDeterminedBy.type,
        dueTimescale: null,
        dueTypeId: newValue,
      }));
    }
  };

  const handleSetDueTimescale = (newTimescale: TimescaleArray) => {
    setFinding((v) => ({
      ...v,
      dueDeterminedBy: DueDeterminedBy.timescale,
      dueTimescale: newTimescale,
      dueTypeId: null,
    }));
  };

  React.useEffect(() => {
    if (incomingValue) {
      setFinding((v) => ({ ...v, ...incomingValue }));
      dispatch(SET_INCOMING_VALUE({ key: incomingValueKey }));
    }
  }, [dispatch, incomingValue, incomingValueKey]);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={`Create Suggested ${typeCapitalised}`}
          onPressSaveButton={handleSave}
          saveButtonText="Done"
          onPressCancelButton={() => dispatch(NAVIGATE_MODAL_BACK())}
        />
      }
    >
      <CardGeneric>
        <Txt>
          If this {type} is likely to be added often, turn it into a suggestion.
          Press done when you&apos;re ready and the suggestion will be available
          for you to use immediately. It will become available to everyone else
          in your organisation to use once your device syncs with RiskBase
        </Txt>
      </CardGeneric>

      {checklistItems.length ? (
        <Card
          explanation={`The ${type} will be suggested when this checklist item is answered with a ${
            type === 'action' ? 'cross' : 'tick'
          }`}
        >
          <CardRow
            label={question.question}
            labelNumberOfLines={0}
            icon={ICONS.CircleCSolid}
            iconColor={theme.colors.checklist}
          />
          {checklistItems.map((checklistItem) => (
            <CardRow
              key={checklistItem.id}
              label={checklistItem.text}
              labelNumberOfLines={0}
              radioValue={checklistItem.id === selectedChecklistItemId}
              onPress={() => setSelectedChecklistItemId(checklistItem.id)}
            />
          ))}
        </Card>
      ) : type === 'action' ? (
        <Card
          explanation={`The ${type} will be suggested when this answer is chosen`}
        >
          <CardRow
            label={question.question}
            labelNumberOfLines={0}
            icon={ICONS.CircleQSolid}
            iconColor={theme.colors.question}
          />
          {answers.map((answer) => (
            <CardRow
              key={answer.id}
              label={answer.answer}
              labelColor={answer.negative ? theme.colors.red : undefined}
              labelNumberOfLines={0}
              radioValue={answer.id === selectedAnswerId}
              onPress={() => setSelectedAnswerId(answer.id)}
            />
          ))}
        </Card>
      ) : (
        <Card>
          <CardRow
            label={question.question}
            labelNumberOfLines={0}
            icon={ICONS.CircleQSolid}
            iconColor={theme.colors.question}
          />
        </Card>
      )}

      <Card
        label={`The ${typeCapitalised}`}
        explanation="Once created, suggestions can be modified and deleted in settings"
      >
        <TextAreaField
          value={finding.description}
          placeholder={`What is the ${type}`}
          name="description"
          onChangeWithName={handleOnChangeWithName}
          onEsc={handleClose}
        />
        <TextAreaField
          value={finding.why}
          placeholder="Why is it required"
          name="why"
          onChangeWithName={handleOnChangeWithName}
          onEsc={handleClose}
        />
      </Card>
      <Card>
        <CardRow
          label="Estimated Cost"
          onPress={() => costFieldRef.current?.focus()}
          contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
          icon={ICONS.SterlingSignLight}
        >
          <TextField
            myRef={costFieldRef}
            value={finding.cost}
            name="cost"
            placeholder="No Cost"
            onChangeWithName={handleOnChangeWithName}
            onEsc={handleClose}
            special={TextFieldSpecial.currency}
            containerStyle={{ backgroundColor: 'transparent', width: 120 }}
            inputStyle={{ textAlign: 'right', paddingRight: 0 }}
          />
        </CardRow>
        <CardRow
          label="Category"
          value={category.name}
          onPress={openCategorySelect}
          icon={ICONS.TagLight}
        />

        {type === 'action' &&
          Object.keys(severities).length > 1 &&
          (currentlyEditing !== 'severity' ? (
            <CardRow
              label="Severity"
              value={severity.name}
              onPress={() => animateNext(() => setCurrentlyEditing('severity'))}
              icon={ICONS.DiamondExclamationSolid}
            />
          ) : (
            <Card
              expandedInline
              margin={false}
              marginHorizontal={false}
              expandedInlineFirst={Object.values(categories).length < 2}
            >
              {Object.values(severities)
                .sort((a, b) => a.score - b.score)
                .map((p) => (
                  <CardRow
                    key={p.id}
                    label={p.name}
                    radioValue={severity.id === p.id}
                    onPress={() => {
                      setFinding((v) => ({ ...v, severityId: p.id }));
                      setCurrentlyEditing(undefined);
                    }}
                  />
                ))}
            </Card>
          ))}

        {type === 'action' &&
          Object.keys(timescales).length > 1 &&
          (currentlyEditing !== 'timescale' ? (
            <CardRow
              label="Timescale"
              value={timescale.name}
              onPress={() =>
                animateNext(() => setCurrentlyEditing('timescale'))
              }
              style={{ flex: 1 }}
              icon={ICONS.HourglassLight}
            />
          ) : (
            <Card
              expandedInline
              margin={false}
              marginHorizontal={false}
              expandedInlineFirst={
                Object.values(categories).length < 2 &&
                Object.keys(severities).length < 2
              }
            >
              {Object.values(timescales)
                .sort((a, b) => a.score - b.score)
                .map((p) => (
                  <CardRow
                    key={p.id}
                    label={p.name}
                    radioValue={timescale.id === p.id}
                    onPress={() => {
                      setFinding((v) => ({ ...v, timescaleId: p.id }));
                      setCurrentlyEditing(undefined);
                    }}
                  />
                ))}
            </Card>
          ))}

        {type === 'action' &&
          (currentlyEditing !== 'due' ? (
            <ViewWithSeparators horizontal>
              <CardRow
                label="Due"
                value={
                  finding.dueDeterminedBy === DueDeterminedBy.timescaleBased
                    ? 'Based on Timescale'
                    : finding.dueDeterminedBy === DueDeterminedBy.timescale
                      ? 'Custom Timescale'
                      : dueType?.name || 'Unknown'
                }
                onPress={() => animateNext(() => setCurrentlyEditing('due'))}
                style={{ flex: 1 }}
                icon={ICONS.CalendarDayLight}
              />
              {finding.dueDeterminedBy === DueDeterminedBy.timescale && (
                <CardRow
                  style={{
                    backgroundColor: 'white',
                  }}
                  contentStyle={{
                    paddingVertical: 0,
                    flexDirection: 'row',
                    flex: Platform.OS === 'web' ? undefined : 0,
                    width: 100,
                  }}
                  childrenStyle={{
                    width: 100,
                  }}
                >
                  <TimescaleField
                    onChange={handleSetDueTimescale}
                    timescale={finding.dueTimescale!}
                  />
                </CardRow>
              )}
            </ViewWithSeparators>
          ) : (
            <Card
              expandedInline
              expandedInlineLast
              margin={false}
              marginHorizontal={false}
            >
              {[
                {
                  name: 'Based on Timescale',
                  id: DueDeterminedBy.timescaleBased,
                  id2: DueDeterminedBy.timescaleBased,
                  id3: null,
                  selected:
                    finding.dueDeterminedBy === DueDeterminedBy.timescaleBased,
                },
                {
                  name: 'Custom Timescale',
                  id: DueDeterminedBy.timescale,
                  id2: DueDeterminedBy.timescale,
                  id3: null,
                  selected:
                    finding.dueDeterminedBy === DueDeterminedBy.timescale,
                },
                ...Object.values(dueTypes)
                  .filter((dt) => dt.id !== '1')
                  .map((dt) => ({
                    name: dt.name,
                    id: DueDeterminedBy.type,
                    id2: dt.id,
                    id3: dt.id,
                    selected: finding.dueTypeId === dt.id,
                  })),
              ].map((p) => (
                <CardRow
                  key={p.id2}
                  label={p.name}
                  radioValue={
                    finding.dueDeterminedBy === p.id &&
                    finding.dueTypeId === p.id3
                  }
                  onPress={() => {
                    handleSetDue(p.id, p.id2);
                    setCurrentlyEditing(undefined);
                  }}
                />
              ))}
            </Card>
          ))}
      </Card>
    </PageScrollView>
  );
};
